import {gql} from '@apollo/client';
import React from 'react';

import {assert} from 'Asserts';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {AnalyticsDashboardSelectorQuery} from 'insights/analytics-dashboards/__generated/AnalyticsDashboardSelectorQuery';
import {AnalyticsParameterValueFragment} from 'insights/parameters/ParameterConstants';
import FieldError from 'ui-library/FieldError';
import useOnChangeOnly from 'ui-library/hooks/useOnChangeOnly';
import useOnMount from 'ui-library/hooks/useOnMount';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import omitKey from 'util/omitKey';

import {AnalyticsParameterValueTemplateGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsParameterValueTemplateGraphQL';
import {EmbeddableParameterType} from '../EmbeddingConstants';
import {
  ParameterValueTemplateFormQuery,
  ParameterValueTemplateFormQueryVariables,
} from './__generated/ParameterValueTemplateFormQuery';
import ParameterValueTemplateFieldList from './ParameterValueTemplateFieldList';
import {ParameterValueTemplateFormState} from './useParameterValueTemplateFormState';

type AnalyticsDashboard = ExtractByTypename<
  ParameterValueTemplateFormQuery['analyticsDashboard'],
  'AnalyticsDashboardNode'
>;

type AnalyticsParameterConfig = AnalyticsDashboard['parameterConfigs'][number];

type SelectorAnalyticsDashboard =
  AnalyticsDashboardSelectorQuery['analyticsDashboardListing']['analyticsDashboards'][number];

export const PARAMETER_VALUE_TEMPLATE_FORM_QUERY = gql`
  query ParameterValueTemplateFormQuery($analyticsDashboardId: StrictString!)
  @skipAudit
  @engComponent(name: INSIGHTS) {
    analyticsDashboard(id: $analyticsDashboardId) {
      ... on AnalyticsDashboardNode {
        id
        parameterValues {
          ...AnalyticsParameterValue
        }
        parameterConfigs {
          ...AnalyticsParameterConfigGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${AnalyticsParameterValueFragment.fragment}
  ${getFragmentWrapper('AnalyticsParameterConfig').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface ParameterValueTemplateFormProps<EmbeddingType extends EmbeddableParameterType> {
  embeddingType: EmbeddingType;
  analyticsDashboard: AnalyticsDashboard;
  parameterTemplateFormState: ParameterValueTemplateFormState;
  initialValueTemplateData: {
    dashboardId: string;
    valueTemplates: Array<AnalyticsParameterValueTemplateGraphQL>;
  } | null;
  focusConfig?: Pick<AnalyticsParameterConfig, 'id'>;
}

function ParameterValueTemplateForm<EmbeddingType extends EmbeddableParameterType>(
  props: ParameterValueTemplateFormProps<EmbeddingType>
): JSX.Element {
  function updateValueTemplates(): void {
    if (
      props.initialValueTemplateData &&
      props.initialValueTemplateData.dashboardId === props.analyticsDashboard.id
    ) {
      props.parameterTemplateFormState.setToValueTemplates(props.initialValueTemplateData.valueTemplates);
    } else {
      props.parameterTemplateFormState.setToDashboardValues(props.analyticsDashboard);
    }
  }
  useOnMount(() => {
    updateValueTemplates();
  });

  useOnChangeOnly(() => {
    updateValueTemplates();
  }, [props.analyticsDashboard]);

  if (props.analyticsDashboard.parameterConfigs.length === 0) {
    return <div>This dashboard has no parameters</div>;
  } else {
    return (
      <ParameterValueTemplateFieldList
        embeddingType={props.embeddingType}
        parameterConfigs={props.analyticsDashboard.parameterConfigs}
        parameterValueTemplateFormState={props.parameterTemplateFormState}
        focusConfig={props.focusConfig}
      />
    );
  }
}

export interface QueryingParameterValueTemplateFormProps<
  EmbeddingType extends EmbeddableParameterType,
> extends Omit<ParameterValueTemplateFormProps<EmbeddingType>, 'analyticsDashboard'> {
  selectedDashboard: SelectorAnalyticsDashboard;
}

export default function QueryingParameterValueTemplateForm<EmbeddingType extends EmbeddableParameterType>(
  props: QueryingParameterValueTemplateFormProps<EmbeddingType>
): JSX.Element {
  const query = useBenchlingQuery<ParameterValueTemplateFormQuery, ParameterValueTemplateFormQueryVariables>(
    PARAMETER_VALUE_TEMPLATE_FORM_QUERY,
    {
      variables: {analyticsDashboardId: props.selectedDashboard.id},
    }
  );

  if (query.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (query.type === QueryResultType.ERROR) {
    return <FieldError message="Error loading embedded dashboard parameter values" />;
  } else {
    const dashboard = query.data.analyticsDashboard;

    // This should never happen as only accessible dashboards are populated in the dashboard selector
    assert(!isInaccessible(dashboard), 'Selected inaccessible dashboard in parameter template config modal');
    return (
      <ParameterValueTemplateForm {...omitKey(props, 'selectedDashboard')} analyticsDashboard={dashboard} />
    );
  }
}
